class ArchiveFilter {
    private readonly form!: HTMLFormElement;
    private readonly formSelector!: string;

    // Scoped to the main archive section: brand/type pages have a second
    // "sold" section (.sold-archive) that also contains .archive-overview and
    // ul.products. Unscoped selectors made renders target the wrong grid after
    // a no-products result.
    private mainContentSelector: string = 'section.woocommerce-archive .archive-overview';
    private productGridSelector: string = 'section.woocommerce-archive .archive-overview ul.products';

    private searchTimer?: number;
    private dropdownCounter: number = 0;
    private dropdownPositionListenersInitialized: boolean = false;
    private sliderListenersBound: boolean = false;
    private initialized: boolean = false;
    private abortController?: AbortController;

    constructor(formSelector: string) {
        const form = document.querySelector<HTMLFormElement>(formSelector);

        if (!form) {
            return;
        }

        this.form = form;
        this.formSelector = formSelector;
        this.init();
    }

    private usesDropdownLayer(): boolean {
        return Boolean(this.dropdownLayer());
    }

    private dropdownLayer(): HTMLElement | null {
        return this.form.querySelector<HTMLElement>('.filter-dropdown-layer');
    }

    private getDropdownContent(filterItem: HTMLElement): HTMLElement | null {
        const dropdownId = filterItem.dataset.dropdownId;

        if (dropdownId) {
            const dropdown = this.form.querySelector<HTMLElement>(`#${CSS.escape(dropdownId)}`);
            if (dropdown) return dropdown;
        }

        return filterItem.querySelector<HTMLElement>('.filter-item__content, :scope > .widget_price_filter');
    }

    private moveDropdownToLayer(filterItem: HTMLElement, content: HTMLElement): void {
        const layer = this.dropdownLayer();
        if (!layer) {
            content.hidden = false;
            return;
        }

        if (!content.id) {
            this.dropdownCounter += 1;
            content.id = `archive-filter-dropdown-${this.dropdownCounter}`;
        }

        filterItem.dataset.dropdownId = content.id;

        if (content.parentElement !== layer) {
            layer.appendChild(content);
        }

        content.hidden = !filterItem.classList.contains('active');
    }

    private positionDropdown(filterItem: HTMLElement): void {
        const content = this.getDropdownContent(filterItem);
        const layer = this.dropdownLayer();
        const toggle = filterItem.querySelector<HTMLElement>('.filter-item__toggle');

        if (!content || !layer || !toggle) return;

        const formRect = this.form.getBoundingClientRect();
        const toggleRect = toggle.getBoundingClientRect();
        const viewportPadding = 16;
        const dropdownWidth = Math.min(content.offsetWidth || 240, window.innerWidth - viewportPadding * 2);
        let left = toggleRect.left - formRect.left;

        if (toggleRect.left + dropdownWidth > window.innerWidth - viewportPadding) {
            left = window.innerWidth - viewportPadding - dropdownWidth - formRect.left;
        }

        left = Math.min(window.innerWidth - (2 * viewportPadding) - dropdownWidth, Math.max(viewportPadding - formRect.left, left));

        content.style.left = `${left}px`;
        content.style.top = `${toggleRect.bottom - formRect.top}px`;
    }

    private closeDropdowns(except?: HTMLElement): void {
        this.form.querySelectorAll<HTMLElement>('.filter-buttons .filter-item').forEach(filterItem => {
            if (filterItem !== except) {
                filterItem.classList.remove('active');
                const dropdown = this.getDropdownContent(filterItem);
                dropdown?.classList.remove('active');

                if (dropdown && this.usesDropdownLayer()) {
                    dropdown.hidden = true;
                }
            }
        });
    }

    private removeDropdown(filterItem: HTMLElement | null): void {
        if (!filterItem) return;

        this.getDropdownContent(filterItem)?.remove();
    }

    // Initialize all filter functionality
    private init(): void {
        if (this.initialized) return;

        this.initialized = true;
        this.removeStaleHiddenInputs();
        this.initItems();
        this.initRadioGroup();
        this.initSlider();
        this.initSearch();
        this.initOutsideClick();
        this.preventSubmit();

        this.form.classList.add('ready');
    }

    /*
     * WooCommerce widgets nested in our form (price filter, catalog ordering)
     * output hidden inputs that reproduce the query string of the page load.
     * Their nested <form> tags collapse into ours, so those stale hidden inputs
     * duplicate our interactive controls (e.g. pa_types=tg5390,tg5390 after
     * comma-joining), which breaks filtering on the server.
     */
    private removeStaleHiddenInputs(): void {
        const duplicatedNames = ['merk', 'pa_types', 'orderby', 'min_price', 'max_price', 'product_search', 'paged'];

        this.form.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach(input => {
            if (duplicatedNames.includes(input.name)) {
                input.remove();
            }
        });
    }

    // Close any open dropdown when clicking outside of it
    private initOutsideClick(): void {
        document.addEventListener('click', (event) => {
            // Nothing open, nothing to do
            if (!this.form.querySelector('.filter-buttons .filter-item.active')) return;

            const target = event.target as HTMLElement | null;

            // Keep open when clicking a filter toggle/item or inside the
            // dropdown content (which may live in the dropdown layer)
            if (target?.closest('.filter-item, .filter-dropdown-layer')) return;

            this.closeDropdowns();
        });
    }

    // Update products when the archive search changes.
    private initSearch(): void {
        const searchInput = this.form.querySelector<HTMLInputElement>('input[name="product_search"]');
        if (!searchInput) return;

        searchInput.addEventListener('input', () => {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(() => {
                this.updateResults();
            }, 350);
        });

        // The search bar is hidden on desktop: clear any search entered on a
        // small screen when resizing above the breakpoint, so users aren't
        // stuck with a filter they can no longer see or clear.
        const desktopBreakpoint = window.matchMedia('(min-width: 575px)');

        desktopBreakpoint.addEventListener('change', (event) => {
            if (!event.matches || !searchInput.value.trim()) return;

            window.clearTimeout(this.searchTimer);
            searchInput.value = '';
            this.updateResults();
        });
    }

    // Set up toggle buttons for filter sections (open/close)
    private initItems(): void {
        const filterItems = this.form.querySelectorAll<HTMLElement>('.filter-buttons .filter-item');

        filterItems?.forEach(filterItem => {
            if (filterItem.dataset.filterInitialized === 'true') return;

            const toggle = filterItem.querySelector<HTMLElement>('.filter-item__toggle');
            const content = filterItem.querySelector<HTMLElement>('.filter-item__content, :scope > .widget_price_filter');

            if (!toggle || !content) return;

            this.moveDropdownToLayer(filterItem, content);
            filterItem.dataset.filterInitialized = 'true';

            // Toggle active state when clicking the button
            toggle.addEventListener('click', () => {
                const dropdown = this.getDropdownContent(filterItem);
                const shouldOpen = !filterItem.classList.contains('active');

                this.closeDropdowns(filterItem);
                filterItem.classList.toggle('active', shouldOpen);

                if (dropdown && this.usesDropdownLayer()) {
                    dropdown.hidden = !shouldOpen;
                    dropdown.classList.toggle('active', shouldOpen);
                } else {
                    dropdown?.classList.toggle('active', shouldOpen);
                }

                if (shouldOpen) {
                    this.positionDropdown(filterItem);
                }
            });

            // Close other sections when hovering over a different one
            toggle.addEventListener('mouseenter', () => {
                this.closeDropdowns(filterItem);
                this.positionDropdown(filterItem);
            });
        })

        if (this.dropdownPositionListenersInitialized) return;

        this.dropdownPositionListenersInitialized = true;

        window.addEventListener('resize', () => {
            const activeFilter = this.form.querySelector<HTMLElement>('.filter-buttons .filter-item.active');
            if (activeFilter) this.positionDropdown(activeFilter);
        });

        this.form.querySelector<HTMLElement>('.filter-buttons-wrapper')?.addEventListener('scroll', () => {
            const activeFilter = this.form.querySelector<HTMLElement>('.filter-buttons .filter-item.active');
            if (activeFilter) this.positionDropdown(activeFilter);
        });
    }

    // Set up radio button groups (like Sort, Type, Brand options)
    private initRadioGroup(container?: Element): void {
        const radios: HTMLElement[] = [];

        if (container && container instanceof Element && container.matches('.filter-radio')) {
            radios.push(container as HTMLElement);
        } else {
            this.form.querySelectorAll<HTMLElement>('.filter-radio').forEach(el => radios.push(el));
        }

        radios.forEach(filterRadio => {
            if (filterRadio.dataset.radioInitialized === 'true') return;

            const content = this.getDropdownContent(filterRadio);
            const items = (content ?? filterRadio).querySelectorAll<HTMLElement>('.sort-option-item');
            const title = filterRadio.querySelector<HTMLElement>(filterRadio.classList.contains('filter-sort') ? 'legend' : '.filter-title');
            const selectedValue = (content ?? filterRadio).querySelector<HTMLInputElement>('input[type="radio"]:checked')?.value ?? null;

            if (!title) return;

            filterRadio.dataset.radioInitialized = 'true';

            items?.forEach((item) => {
                const input = item.querySelector<HTMLInputElement>('input[type="radio"]');

                if (!input) {
                    return;
                }

                input.addEventListener('change', () => {
                    if (!input.checked) {
                        return;
                    }

                    const isResetOption = item.classList.contains('sort-option-item__reset');
                    const isSortFilter = filterRadio.classList.contains('filter-sort');
                    const isBrandFilter = filterRadio.classList.contains('filter-brands');
                    const valueChanged = input.value !== selectedValue;

                    // Update the toggle label
                    const selectedLabel = item.textContent?.trim() ?? '';

                    if (isSortFilter) {
                        title.innerHTML = selectedLabel;
                    } else {
                        title.innerHTML = isResetOption ? '' : `: ${selectedLabel}`;
                    }

                    // Changing or clearing the brand invalidates the selected type
                    if (isBrandFilter && (isResetOption || valueChanged)) {
                        const typeFilter = this.form.querySelector<HTMLElement>('.filter-types');
                        const selectedType = typeFilter
                            ? this.getDropdownContent(typeFilter)?.querySelector<HTMLInputElement>(
                                '.sort-option-item input[type="radio"]:checked'
                            )
                            : null;

                        if (selectedType) {
                            selectedType.checked = false;
                        }
                    }

                    this.updateResults();
                });
            });
        });
    }

    // Set up the price range slider
    private initSlider(): void {
        // Global jQuery events: bind once per instance. initSlider() runs again
        // after every slider re-render, and these listeners live on document.body,
        // so re-binding would stack duplicate handlers.
        if (this.sliderListenersBound) return;

        if (!this.form.querySelector('.filter-slider')) return;

        this.sliderListenersBound = true;

        // Update results when the price slider finishes changing
        (window as any).jQuery(document.body).on('price_slider_change', () => {
            this.updateResults();
        });

        // Update the displayed price values while sliding
        (window as any).jQuery(document.body).on('price_slider_slide', () => {
            this.updateSliderLabels();
        });

        this.updateSliderLabels();
    }

    // Update the price labels next to the slider. Inputs are looked up on each
    // call because the slider DOM is replaced after every AJAX update.
    private updateSliderLabels(): void {
        const slider = this.form.querySelector<HTMLElement>('.filter-slider');
        if (!slider) return;

        const content = this.getDropdownContent(slider) ?? slider;
        const format = (value: string) => Intl.NumberFormat(navigator.language, {
            style: 'currency',
            currency: 'EUR'
        }).format(parseFloat(value));

        const minPriceInput = content.querySelector<HTMLInputElement>('input[name="min_price"]');
        const maxPriceInput = content.querySelector<HTMLInputElement>('input[name="max_price"]');
        const minPriceLabel = content.querySelector<HTMLElement>('label[for="min_price"]');
        const maxPriceLabel = content.querySelector<HTMLElement>('label[for="max_price"]');

        if (minPriceLabel && minPriceInput) {
            minPriceLabel.innerHTML = format(minPriceInput.value);
        }

        if (maxPriceLabel && maxPriceInput) {
            maxPriceLabel.innerHTML = format(maxPriceInput.value);
        }
    }

    // Prevent form from submitting normally
    private preventSubmit(): void {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.updateResults();
        });
    }

    // Check which taxonomy page we are on (category or brand)
    private currentTaxonomy(): string | null {
        let taxonomy = null;

        if (document.body.classList.contains('tax-product_cat')) {
            taxonomy = 'product_cat';
        } else if (document.body.classList.contains('tax-product_brand')) {
            taxonomy = 'merk';
        } else if (document.body.classList.contains('page-template-template-sold')) {
            taxonomy = 'sold';
        }

        return taxonomy;
    }

    // Collect the form state and build the target URL + whether a reload is needed
    private buildUrl(): { url: string, reload: boolean } {
        const taxonomy = this.currentTaxonomy();
        const formData = new FormData(this.form);

        // Combine multiple values for the same filter (like multiple checkboxes)
        const combinedFormData = new Map<string, string>();
        formData?.forEach((value, key) => {
            if (combinedFormData.has(key)) {
                combinedFormData.set(key, combinedFormData.get(key) + ',' + value);
            } else {
                combinedFormData.set(key, value.toString());
            }
        });

        const params = new URLSearchParams(combinedFormData as any);
        let path = window.location.pathname;
        let reload = false;

        if (!params.get('product_search')?.trim()) {
            params.delete('product_search');
        }

        // Get the selected brand from filters
        const firstBrand = params.get('merk')?.split(',')[0] ?? null;

        // Different behavior based on current page type
        if (taxonomy === 'product_cat') {
            // On a category page: keep the category path and add brand as parameter
            const termClass = document.body.className.split(' ').find(c => c.startsWith('term-'));
            const term = termClass?.replace('term-', '');

            reload = !path.includes(`/${term}/`);
            path = `/${term}/`;

            if (firstBrand === '') {
                params.delete('merk');
            }
        } else if (taxonomy === 'sold') {
            path = '/verkocht/';

            if (firstBrand === '') {
                params.delete('merk');
            }
        } else if (params.has('merk')) {
            // On shop or brand page
            if (firstBrand) {
                // Brand selected: go to the brand page
                reload = !path.includes(`/merken/${firstBrand}/`);
                path = `/merken/${firstBrand}/`;
            } else {
                // No brand selected: go back to shop page
                reload = path.includes('/merken/');
                path = '/aanbod/';
            }

            params.delete('merk');
        }

        // Remove empty pa_types parameter
        if (params.get('pa_types') === '') {
            params.delete('pa_types');
        }

        // Ensure path ends with a slash
        if (!path.endsWith('/')) {
            path = path + '/';
        }

        return { url: `${path}?${params.toString()}`, reload };
    }

    // Main method to update products based on filter selections
    private async updateResults(): Promise<void> {
        const { url, reload } = this.buildUrl();

        // Full page reload if path changed
        if (reload) {
            window.location.href = url;
            return;
        }

        // Abort any in-flight request: the latest filter state wins. This also
        // means rapid interactions are never silently dropped.
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        this.form.classList.add('updating');
        document.querySelector(this.productGridSelector)?.classList.add('opacity-50', 'pointer-events-none');

        try {
            const response = await fetch(url, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                signal: controller.signal
            });

            if (!response.ok) {
                console.error('Network response was not ok', response.status);
                window.location.href = url;
                return;
            }

            const html = await response.text();

            // Parse the response once and update all page sections
            const doc = new DOMParser().parseFromString(html, 'text/html');

            this.renderProducts(doc);
            this.renderTypes(doc);
            this.renderMinMaxPrice(doc);
            this.renderHeader(doc);

            // Update URL without page reload
            window.history.pushState({}, '', url);
        } catch (error) {
            // A newer request replaced this one: let it handle the UI.
            if ((error as Error)?.name === 'AbortError') return;

            console.error('Error fetching products:', error);
            window.location.href = url;
        } finally {
            // Only the latest request may clear the loading state
            if (this.abortController === controller) {
                document.querySelector(this.productGridSelector)?.classList.remove('opacity-50', 'pointer-events-none');
                this.form.classList.remove('updating');
                this.closeDropdowns();
            }
        }
    }

    // Replace the product overview with the fetched one. The server response
    // always contains the correct state — product grid + pagination, or the
    // no-products message — so swapping the whole block can never leave a
    // stale "Geen producten gevonden" message behind.
    private renderProducts(doc: Document): void {
        const newOverview = doc.querySelector(this.mainContentSelector);
        const currentOverview = document.querySelector(this.mainContentSelector);

        if (newOverview && currentOverview) {
            currentOverview.replaceWith(newOverview);
            window.dispatchEvent(new CustomEvent('archive-products-updated'));
        }
    }

    // Update the product types filter (if available)
    private renderTypes(doc: Document): void {
        const newTypes = doc.querySelector(`${this.formSelector} .filter-types`);
        const currentTypes = this.form.querySelector<HTMLElement>('.filter-types');

        if (newTypes) {
            if (currentTypes) {
                this.removeDropdown(currentTypes);
                currentTypes.replaceWith(newTypes);
            } else {
                const filterButtons = this.form.querySelector('.filter-buttons');
                if (!filterButtons) return;
                filterButtons.insertBefore(newTypes, filterButtons.querySelector('.filter-price, .filter-reset'));
            }

            this.initItems();
            this.initRadioGroup(newTypes);
        } else if (currentTypes) {
            this.removeDropdown(currentTypes);
            currentTypes.remove();
        }
    }

    // Update the price range slider with new min/max values
    private renderMinMaxPrice(doc: Document): void {
        const newSlider = doc.querySelector(`${this.formSelector} .filter-slider`);
        const currentSlider = this.form.querySelector<HTMLElement>('.filter-slider');

        if (newSlider) {
            if (currentSlider) {
                this.removeDropdown(currentSlider);
                currentSlider.replaceWith(newSlider);
            } else {
                const filterButtons = this.form.querySelector('.filter-buttons');
                if (!filterButtons) return;
                filterButtons.appendChild(newSlider);
            }

            // The fresh widget HTML carries WooCommerce's hidden query-string fields
            this.removeStaleHiddenInputs();
            this.initItems();
            this.updateSliderLabels();

            // Re-initialize price filter functionality (jQuery UI slider)
            (window as any).jQuery(document.body).trigger('init_price_filter');
        } else if (currentSlider) {
            this.removeDropdown(currentSlider);
            currentSlider.remove();
        }
    }

    // Update the page header (title, description)
    private renderHeader(doc: Document): void {
        const newHeader = doc.querySelector('section.woocommerce-archive .archive-header');
        const currentHeader = document.querySelector('section.woocommerce-archive .archive-header');

        // Only update if content changed
        if (newHeader && currentHeader && (newHeader.textContent?.trim() !== currentHeader.textContent?.trim())) {
            currentHeader.replaceWith(newHeader);
        }
    }
}

window.addEventListener('load', () => {
    new ArchiveFilter('form.archive-filter');
});
